/**
 * Einfache Listen (ListType `simple`) waren historisch reine `string[]`. Damit
 * pro Eintrag eine **Erklaerung** (z.B. fuer die Hilfen) gepflegt werden kann,
 * darf ein Eintrag jetzt ALTERNATIV ein Objekt `{ wert, erklaerung }` sein.
 *
 * Reine Strings bleiben erlaubt und sind der Default (rueckwaertskompatibel +
 * schlanke Liste): ein Eintrag wird erst dann zum Objekt, wenn tatsaechlich eine
 * Erklaerung existiert. Eine Liste kann also gemischt String- und Objekt-Eintraege
 * enthalten.
 *
 * Dropdowns/Validierung sollen weiterhin NUR die Werte sehen — dafuer
 * normalisiert `DataService.getHardcoded()` (bzw. die Humusform-Ableitung)
 * simple-Listen ueber {@link listValuesForRuntime} / {@link simpleListValue}.
 */

export interface SimpleListEntry {
  wert: string;
  erklaerung?: string;
}

export type SimpleListItem = string | SimpleListEntry;

/** Reiner Wert eines simple-Eintrags (String bleibt String, Objekt -> `.wert`). */
export function simpleListValue(item: SimpleListItem): string {
  return typeof item === 'string' ? item : (item?.wert ?? '');
}

/** Erklaerung eines simple-Eintrags (`''` bei reinem String / ohne Erklaerung). */
export function simpleListExplanation(item: SimpleListItem): string {
  return typeof item === 'string' ? '' : (item?.erklaerung ?? '');
}

/**
 * Baut den kompaktesten Eintrag: ohne Erklaerung ein reiner String, mit
 * Erklaerung ein `{ wert, erklaerung }`-Objekt. Wert und Erklaerung werden
 * getrimmt; eine leere Erklaerung faellt weg (Eintrag bleibt reiner String).
 */
export function makeSimpleEntry(wert: string, erklaerung?: string): SimpleListItem {
  const w = (wert ?? '').trim();
  const e = (erklaerung ?? '').trim();
  return e ? { wert: w, erklaerung: e } : w;
}

/**
 * Laufzeit-Normalisierung fuer den Konsum in Formularen/Dropdowns/Validierung:
 * `simple`-Listen werden auf reine Werte abgebildet, alle anderen Listentypen
 * unveraendert durchgereicht.
 */
export function listValuesForRuntime(type: string, data: any[]): any[] {
  return type === 'simple' ? (data ?? []).map(simpleListValue) : (data ?? []);
}

/**
 * Bereitet ein hochgeladenes JSON-Array als `simple`-Liste auf (fuer den
 * **ersetzenden** Import): trimmt Werte, wirft leere/whitespace-Eintraege raus
 * und entfernt Dubletten INNERHALB der Datei (erster Treffer gewinnt, Dedup
 * ueber {@link simpleListValue}).
 *
 * Bewusst ein REPLACE und kein Merge: die Forstaemter-Liste z.B. ist eine
 * Spiegelung der distinct `FORSTAMT`-Werte der Reviere und muss exakt dem
 * (numerierten) Soll entsprechen — ein Merge gegen einen stale, anders
 * formatierten DB-Stand ("Rothemuehl" vs. "02 Rothemuehl") erzeugt nur
 * Dubletten statt Konsistenz.
 *
 * Akzeptiert je Eintrag einen reinen String ODER ein `{ wert, erklaerung }`-
 * Objekt (wie es eine simple-Liste selbst traegt).
 */
export function normalizeSimpleListImport(imported: unknown[]): SimpleListItem[] {
  const seen = new Set<string>();
  const out: SimpleListItem[] = [];
  for (const raw of imported ?? []) {
    const wert = simpleListValue(raw as SimpleListItem).trim();
    if (!wert || seen.has(wert)) continue;
    seen.add(wert);
    out.push(makeSimpleEntry(wert, simpleListExplanation(raw as SimpleListItem)));
  }
  return out;
}
